import { RootState } from '@AppState/redux-store'
import { WithPermissions } from '@Components/common/with-permissions'
import {
  AuthScopes,
  PluginType,
  PromotionCampaign,
  PromotionCampaignAward,
  PromotionRewardCategory,
} from '@Generated/back-office-client/src'
import { orderBy } from 'lodash'
import { Button, Container, Flex, Section, Text, Heading, Skeleton } from '@radix-ui/themes'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { toZonedTime, formatInTimeZone } from 'date-fns-tz'
import { PromotionCampaignsServiceAPI } from '@API/data-services/promotion-campaigns-service'
import { GamesServiceAPI } from '@API/data-services/games-service'
import { PluginMetadataServiceAPI } from '@API/data-services/plugin-metadata-service'
import { PluginServiceAPI } from '@API/data-services/plugin-service'
import { PromotionCampaignFormData } from './types'
import { formatEnumLabel } from './utils'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { PromotionCampaignCard } from './promotion-campaign-card'
import { PromotionCampaignFormDrawer } from './promotion-campaign-form-drawer'
import { DeleteConfirmationDialog } from './components/delete-confirmation-dialog'
import './styles.scss'

export const PromotionCampaignsAdmin = () => {
  const { activeCustomerId, activeCustomerMetadata } = useSelector(
    (state: RootState) => state.users,
  )
  const queryClient = useQueryClient()
  const [editingCampaign, setEditingCampaign] = useState<PromotionCampaignFormData | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [deletingCampaign, setDeletingCampaign] = useState<PromotionCampaign | null>(null)
  const [campaigns, setCampaigns] = useState<PromotionCampaign[]>([])
  const timezone = activeCustomerMetadata?.timezone || 'America/New_York'

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const { isLoading: campaignsIsLoading } = useQuery({
    queryKey: [`${activeCustomerId}-promotion-campaigns`],
    refetchOnMount: 'always',
    queryFn: async () => {
      const data = await PromotionCampaignsServiceAPI.getPromotionCampaigns()
      // Sort by priority descending, with null priorities (ended campaigns) at the end
      const sorted = orderBy(data, (campaign) => campaign.priority ?? -Infinity, 'desc')
      setCampaigns(sorted)
      return sorted
    },
  })

  const { data: allGamesData } = useQuery({
    queryKey: [`${activeCustomerId}-all-games`],
    queryFn: async () => {
      return await GamesServiceAPI.getAllGames(false, true, false)
    },
  })

  const { data: pluginMappings, isLoading: pluginMappingsLoading } = useQuery({
    queryKey: [`${activeCustomerId}-plugin-mappings`],
    queryFn: async () => {
      return await PluginServiceAPI.listMappings()
    },
    enabled: !!activeCustomerId,
  })

  const gameOptions = useMemo(() => {
    if (!allGamesData) {
      return []
    }

    const games = [
      ...(allGamesData.drawGames || []).map((g) => ({
        value: g.id,
        label: g.name || 'Unnamed Game',
        subtitle: g.gameId ?? g.id,
      })),
      ...(allGamesData.instantGames || []).map((g) => ({
        value: g.id,
        label: g.name || 'Unnamed Game',
        subtitle: g.gameId ?? g.id,
      })),
      ...(allGamesData.fastPlayGames || [])
        .filter((g) => g.fastPlayGame)
        .map((g) => ({
          value: g.fastPlayGame.id,
          label: g.fastPlayGame.name || 'Unnamed Game',
          subtitle: g.fastPlayGame.gameId ?? g.fastPlayGame.id,
        })),
      ...(allGamesData.rapidGames || []).map((g) => ({
        value: g.id,
        label: g.name || 'Unnamed Game',
        subtitle: g.gameId ?? g.id,
      })),
    ]

    return games.filter((g) => g.value && g.label).sort((a, b) => a.label.localeCompare(b.label))
  }, [allGamesData])

  const awardTypeOptions = useMemo(() => {
    if (!pluginMappings) {
      return []
    }
    return pluginMappings
      .filter((m) => m.type === PluginType.REWARD_ASSIGNMENT && m.refId)
      .map((m) => ({
        value: m.refId as string,
        label: formatEnumLabel(m.refId as string),
      }))
  }, [pluginMappings])

  const { data: pluginMetadataSchema } = useQuery({
    queryKey: [
      `plugin-metadata-schema-REWARD_ASSIGNMENT-${editingCampaign?.awardConfig?.type}-${editingCampaign?.trigger}`,
    ],
    queryFn: async () => {
      if (!editingCampaign?.awardConfig?.type) {
        return null
      }
      return await PluginMetadataServiceAPI.getMetadataSchema(
        'REWARD_ASSIGNMENT',
        editingCampaign.awardConfig.type,
        editingCampaign.trigger,
      )
    },
    enabled: !!editingCampaign?.awardConfig?.type,
  })

  const createMutation = useMutation({
    mutationFn: (campaign: PromotionCampaignFormData) =>
      PromotionCampaignsServiceAPI.createPromotionCampaign(campaign),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`${activeCustomerId}-promotion-campaigns`] })
      setIsCreating(false)
      setEditingCampaign(null)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, campaign }: { id: string; campaign: PromotionCampaignFormData }) =>
      PromotionCampaignsServiceAPI.updatePromotionCampaign(id, campaign),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`${activeCustomerId}-promotion-campaigns`] })
      setEditingCampaign(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => PromotionCampaignsServiceAPI.deletePromotionCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`${activeCustomerId}-promotion-campaigns`] })
      setDeletingCampaign(null)
    },
  })

  const updatePrioritiesMutation = useMutation({
    mutationFn: (priorities: Array<{ id: string; priority: number }>) =>
      PromotionCampaignsServiceAPI.updatePromotionPriorities(priorities),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`${activeCustomerId}-promotion-campaigns`] })
    },
    onError: (error) => {
      console.error('Update priorities failed:', error)
      alert('Failed to update campaign priorities. Please try again.')
    },
  })

  const getStatus = useCallback(
    (campaign: PromotionCampaign) => {
      const now = toZonedTime(new Date(), timezone)
      if (!campaign.startAt) {
        return 'Active'
      }
      const startDate = new Date(campaign.startAt)
      if (startDate > now) {
        return 'Scheduled'
      }
      if (campaign.endAt) {
        const endDate = new Date(campaign.endAt)
        if (endDate < now) {
          return 'Ended'
        }
      }
      return 'Active'
    },
    [timezone],
  )

  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case 'Active':
        return 'green'
      case 'Scheduled':
        return 'blue'
      case 'Ended':
        return 'gray'
      default:
        return 'gray'
    }
  }, [])

  // Split campaigns into active/scheduled and ended
  const { activeCampaigns, endedCampaigns } = useMemo(() => {
    const now = toZonedTime(new Date(), timezone)
    const active: PromotionCampaign[] = []
    const ended: PromotionCampaign[] = []

    campaigns.forEach((campaign) => {
      if (campaign.endAt && new Date(campaign.endAt) < now) {
        ended.push(campaign)
      } else {
        active.push(campaign)
      }
    })

    return { activeCampaigns: active, endedCampaigns: ended }
  }, [campaigns, timezone])

  const hasStarted = (campaign: PromotionCampaignFormData | null): boolean => {
    if (!campaign) {
      return false
    }
    // If startAt is null, the promotion is always live (already started)
    if (!campaign.startAt) {
      return true
    }
    return new Date(campaign.startAt) <= toZonedTime(new Date(), timezone)
  }

  const createEmptyCampaign = (): PromotionCampaignFormData => {
    return {
      customerId: activeCustomerId ?? '',
      name: '',
      description: '',
      startAt: null,
      endAt: null,
      trigger: 'TICKET_SCAN',
      triggerCount: 1,
      filters: null,
      redemptionLimitFrequency: null,
      awardConfig: {
        type: (awardTypeOptions[0]?.value || '') as PromotionRewardCategory,
        description: '',
        config: {
          amount: {
            value: 0,
            mode: 'STATIC',
          },
        },
      } as PromotionCampaignAward,
      priority: activeCampaigns.length + 1,
      createdAt: new Date(),
      updatedAt: new Date(),
    } as PromotionCampaignFormData
  }

  const toCampaignFormData = (campaign: PromotionCampaign): PromotionCampaignFormData => ({
    ...campaign,
    startAt: campaign.startAt
      ? formatInTimeZone(new Date(campaign.startAt), timezone, 'yyyy-MM-dd')
      : null,
    endAt: campaign.endAt
      ? formatInTimeZone(new Date(campaign.endAt), timezone, 'yyyy-MM-dd')
      : null,
  })

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setCampaigns((allItems) => {
        // Only reorder active campaigns, keep ended campaigns unchanged
        const now = toZonedTime(new Date(), timezone)
        const active = allItems.filter((c) => !c.endAt || new Date(c.endAt) >= now)
        const ended = allItems.filter((c) => c.endAt && new Date(c.endAt) < now)

        const oldIndex = active.findIndex((i) => i.id === event.active.id)
        const newIndex = active.findIndex((i) => i.id === event.over!.id)
        const reordered = arrayMove(active, oldIndex, newIndex)

        // Simple: assign priorities 1, 2, 3... based on display order
        const updatedWithPriorities = reordered.map((campaign, index) => ({
          ...campaign,
          priority: index + 1,
        }))

        // Send priority updates to API
        updatePrioritiesMutation.mutate(
          reordered.map((campaign, index) => ({
            id: campaign.id!,
            priority: index + 1,
          })),
        )

        // Return combined list: active + ended
        return [...updatedWithPriorities, ...ended]
      })
    }
  }

  const handleSaveEdit = () => {
    if (editingCampaign) {
      if (isCreating) {
        createMutation.mutate(editingCampaign)
      } else {
        updateMutation.mutate({ id: editingCampaign.id, campaign: editingCampaign })
      }
    }
  }

  const handleDelete = () => {
    if (deletingCampaign) {
      deleteMutation.mutate(deletingCampaign.id)
    }
  }

  return (
    <Container>
      <Section>
        <Flex direction="column" gap="4">
          <Flex justify="between" align="center">
            <Heading size="6">Manage Promotions</Heading>
            <WithPermissions requiredPermissions={[AuthScopes.writepromotion_campaigns]}>
              <Flex justify="end">
                <Button
                  onClick={() => {
                    setIsCreating(true)
                    setEditingCampaign(createEmptyCampaign())
                  }}>
                  Create Promotion
                </Button>
              </Flex>
            </WithPermissions>
          </Flex>

          {campaignsIsLoading ? (
            <Skeleton loading={true}>
              <Flex direction="column" gap="2">
                <div className="skeleton-card" />
                <div className="skeleton-card" />
                <div className="skeleton-card" />
              </Flex>
            </Skeleton>
          ) : campaigns.length > 0 ? (
            <Flex direction="column" gap="6">
              {/* Active and Scheduled Campaigns */}
              {activeCampaigns.length > 0 && (
                <Flex direction="column" gap="2">
                  <Text size="2" weight="bold" color="gray">
                    Active & Scheduled Campaigns
                  </Text>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}>
                    <SortableContext items={activeCampaigns} strategy={verticalListSortingStrategy}>
                      {activeCampaigns.map((campaign) => (
                        <PromotionCampaignCard
                          key={campaign.id}
                          campaign={campaign}
                          status={getStatus(campaign)}
                          statusColor={getStatusColor(getStatus(campaign))}
                          timezone={timezone}
                          onEdit={() => setEditingCampaign(toCampaignFormData(campaign))}
                          onDelete={() => setDeletingCampaign(campaign)}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </Flex>
              )}

              {/* Ended Campaigns */}
              {endedCampaigns.length > 0 && (
                <Flex direction="column" gap="2">
                  <Text size="2" weight="bold" color="gray">
                    Ended Campaigns
                  </Text>
                  {endedCampaigns.map((campaign) => (
                    <div key={campaign.id} className="ended-campaign">
                      <PromotionCampaignCard
                        campaign={campaign}
                        status={getStatus(campaign)}
                        statusColor={getStatusColor(getStatus(campaign))}
                        timezone={timezone}
                        isDraggable={false}
                        onEdit={() => setEditingCampaign(toCampaignFormData(campaign))}
                        onDelete={() => setDeletingCampaign(campaign)}
                      />
                    </div>
                  ))}
                </Flex>
              )}
            </Flex>
          ) : (
            <Flex direction="column" align="center" gap="2" py="8">
              <Text size="4" weight="medium">
                No promotion campaigns yet
              </Text>
              <Text size="2" color="gray">
                Create your first promotion campaign to get started
              </Text>
            </Flex>
          )}
        </Flex>

        <PromotionCampaignFormDrawer
          campaign={editingCampaign}
          isCreating={isCreating}
          isSaving={createMutation.isPending || updateMutation.isPending}
          gameOptions={gameOptions}
          awardTypeOptions={awardTypeOptions}
          awardTypeOptionsLoading={pluginMappingsLoading}
          pluginMetadataSchema={pluginMetadataSchema ?? null}
          hasStarted={hasStarted(editingCampaign)}
          onClose={() => {
            setEditingCampaign(null)
            setIsCreating(false)
          }}
          onSave={handleSaveEdit}
          onChange={setEditingCampaign}
        />

        <DeleteConfirmationDialog
          campaign={deletingCampaign}
          isDeleting={deleteMutation.isPending}
          onCancel={() => setDeletingCampaign(null)}
          onConfirm={handleDelete}
        />
      </Section>
    </Container>
  )
}
